import { createGlobalStyle } from "styled-components";

const GlobalStyle = createGlobalStyle`
  :root {
    --ink: #102033;
    --muted: #526176;
    --paper: #fffaf1;
    --paper-strong: #ffffff;
    --line: rgba(16, 32, 51, 0.14);
    --blue: #1d5fd1;
    --blue-deep: #123a79;
    --gold: #d99428;
    --green: #147a4d;
    --shadow: 0 26px 90px rgba(30, 54, 87, 0.18);
    --radius-xl: 34px;
    --radius-md: 18px;
    --h1-size: clamp(3rem, 8vw, 7.5rem);
    --body-size: clamp(1rem, 1.4vw, 1.25rem);
  }

  *, *::before, *::after {
    box-sizing: border-box;
  }

  html,
  body,
  #root {
    min-height: 100%;
  }

  body {
    margin: 0;
    background:
      radial-gradient(circle at 12% 18%, rgba(29, 95, 209, 0.18), transparent 30%),
      radial-gradient(circle at 88% 0%, rgba(217, 148, 40, 0.18), transparent 28%),
      linear-gradient(135deg, #eef5ff 0%, var(--paper) 52%, #f4efe6 100%);
    color: var(--ink);
    font-family: "Aptos", "Segoe UI", "Helvetica Neue", Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    text-rendering: optimizeLegibility;
  }

  a {
    color: inherit;
  }

  .placeholder-page {
    min-height: 100vh;
  }

  .placeholder-hero {
    min-height: 100vh;
    display: grid;
    place-items: center;
    padding: clamp(1rem, 4vw, 4rem);
  }

  .placeholder-shell {
    width: min(1180px, 100%);
    border: 1px solid var(--line);
    border-radius: var(--radius-xl);
    background: rgba(255, 255, 255, 0.78);
    box-shadow: var(--shadow);
    overflow: hidden;
    backdrop-filter: blur(18px);
  }

  .brand-row {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: clamp(1.25rem, 3vw, 2rem);
    border-bottom: 1px solid var(--line);
  }

  .brand-mark {
    width: 56px;
    height: 56px;
    display: grid;
    place-items: center;
    border-radius: 18px;
    background: linear-gradient(135deg, var(--blue), var(--blue-deep));
    color: white;
    font-weight: 900;
    letter-spacing: 0.04em;
  }

  .eyebrow,
  .brand-subtitle,
  .status-pill,
  .card-label {
    margin: 0;
  }

  .eyebrow {
    font-size: 0.9rem;
    font-weight: 900;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--blue-deep);
  }

  .brand-subtitle {
    margin-top: 0.2rem;
    color: var(--muted);
    font-size: 0.95rem;
  }

  .hero-grid {
    display: grid;
    grid-template-columns: minmax(0, 1.3fr) minmax(280px, 0.7fr);
    gap: clamp(1.5rem, 5vw, 4rem);
    padding: clamp(1.5rem, 5vw, 5rem);
  }

  .status-pill {
    display: inline-flex;
    width: fit-content;
    align-items: center;
    border: 1px solid rgba(20, 122, 77, 0.24);
    border-radius: 999px;
    background: rgba(20, 122, 77, 0.1);
    color: var(--green);
    font-size: 0.8rem;
    font-weight: 900;
    letter-spacing: 0.12em;
    padding: 0.55rem 0.8rem;
    text-transform: uppercase;
  }

  h1 {
    max-width: 850px;
    margin: 1.2rem 0 1rem;
    font-size: var(--h1-size);
    line-height: 0.88;
    letter-spacing: -0.075em;
  }

  .hero-lede {
    max-width: 720px;
    margin: 0;
    color: var(--muted);
    font-size: var(--body-size);
    line-height: 1.65;
  }

  .hero-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.9rem;
    margin-top: 2rem;
  }

  .primary-action,
  .secondary-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 52px;
    border-radius: 999px;
    font-weight: 900;
    padding: 0 1.15rem;
    text-decoration: none;
  }

  .primary-action {
    background: var(--blue);
    color: white;
    box-shadow: 0 14px 36px rgba(29, 95, 209, 0.25);
  }

  .secondary-action {
    border: 1px solid var(--line);
    background: rgba(255, 255, 255, 0.72);
    color: var(--ink);
  }

  .proof-card {
    align-self: stretch;
    border: 1px solid var(--line);
    border-radius: var(--radius-md);
    background:
      linear-gradient(145deg, rgba(16, 32, 51, 0.94), rgba(18, 58, 121, 0.88)),
      var(--ink);
    color: white;
    padding: clamp(1.25rem, 3vw, 2rem);
  }

  .card-label {
    color: rgba(255, 255, 255, 0.66);
    font-size: 0.78rem;
    font-weight: 900;
    letter-spacing: 0.16em;
    text-transform: uppercase;
  }

  .proof-card ul {
    display: grid;
    gap: 0.85rem;
    margin: 1.4rem 0 0;
    padding: 0;
    list-style: none;
  }

  .proof-card li {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.08);
    padding: 0.9rem 1rem;
  }

  .proof-card span {
    color: rgba(255, 255, 255, 0.72);
  }

  .proof-card strong {
    color: #fff2c2;
  }

  @media (max-width: 820px) {
    .hero-grid {
      grid-template-columns: 1fr;
    }

    .brand-row {
      align-items: flex-start;
    }

    .hero-actions {
      flex-direction: column;
    }

    .primary-action,
    .secondary-action {
      width: 100%;
    }
  }
`;

export default GlobalStyle;
